import { Icon } from '@aibees/shared/components';
import { useDebounceFn } from '@aibees/shared/hooks';
import { useRedirect } from '@aibees/shared/router';
import { searchText } from '@aibees/shared/utils';
import { Box, Divider, TextFieldProps, Typography } from '@mui/material';
import { useState } from 'react';
import { SettingsMenuItem } from './components/settings-menu-item';
import * as Styled from './styles';
import * as SX from './sx';
import {
  AccountMenuItemType,
  type AccountMenuItem,
  type SettingsMenuItemsProps as Props,
} from './types';

export const SettingsMenuItems = ({
  items,
  onClickItem,
  searchable,
}: Props) => {
  const redirect = useRedirect();

  const [searchResult, setSearchResult] = useState(items);

  const handleClick = (path: string, item?: AccountMenuItem) => {
    if (onClickItem) onClickItem();
    if (item?.externalLink && path) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      setTimeout(() => {
        redirect(path);
      });
    }
  };

  const handleSearch: TextFieldProps['onChange'] = useDebounceFn((e) => {
    const search = e.target.value;
    if (!search.length) setSearchResult(items);
    else
      setSearchResult(
        items?.filter(
          (item) => item.linkTo && searchText(item.label, search) > -1
        )
      );
  }, 300);

  return (
    <>
      {searchable && (
        <Box sx={SX.inputWrapper}>
          <Styled.SearchInput
            autoComplete="off"
            variant="outlined"
            size="small"
            placeholder="Search for settings..."
            onChange={handleSearch}
          />
        </Box>
      )}
      {!!searchResult?.length &&
        searchResult?.map((option, index) => {
          if (option.type === AccountMenuItemType.DIVIDER)
            return <Divider key={index} sx={SX.divider} />;
          if (option.blank && option.linkTo)
            return (
              <Styled.MenuLink key={index} to={option.linkTo} target="_blank">
                {!!option.icon && <Icon name={option.icon} color="primary" />}{' '}
                {option.label}
              </Styled.MenuLink>
            );
          return option.type === AccountMenuItemType.LINK ? (
            <SettingsMenuItem
              key={index}
              item={option}
              onClick={(path, menuItem) => handleClick(path, menuItem)}
            />
          ) : (
            <Styled.MenuItemTitle key={index}>
              <Typography sx={SX.label}>{option.label}</Typography>
            </Styled.MenuItemTitle>
          );
        })}
    </>
  );
};

SettingsMenuItems.displayName = 'SettingsMenuItems';
